import { useEffect, useState } from 'react';
import { ActivityIndicator, Alert, Image, Platform, Pressable, ScrollView, Text, View } from 'react-native';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import * as ImagePicker from 'expo-image-picker';
import * as Clipboard from 'expo-clipboard';
import { useAuth } from '../context/AuthContext';
import {
  updatePayoutInfo,
  purchaseVip,
  VipPlan,
  uploadIdentityPhoto,
  requestPhoneOtp,
  verifyPhoneOtp,
  requestEmailOtp,
  verifyEmailOtp,
  getIdentityPhotoToken,
  deleteOwnAccount,
  uploadAvatar,
} from '../api/users';
import {
  Button,
  Card,
  Checkbox,
  ConfirmDialog,
  ErrorText,
  FormInput,
  GradientCard,
  IconBadge,
  Label,
  ScreenContainer,
} from '../components/ui';
import { CameraCapture } from '../components/CameraCapture';
import { useThemeMode } from '../context/ThemeContext';
import { colors, fonts, gradients } from '../theme';
import { ApiError } from '../api/client';
import { openExternalUrl } from '../utils/openUrl';
import { myIdentityPhotoUrl, resolvePhotoUrl } from '../config';
import { OWNER_TIER_LABELS, REVIEWER_TIER_LABELS } from '../constants/labels';

function SectionTitle({ icon, tone, children }: { icon: keyof typeof Ionicons.glyphMap; tone?: 'primary' | 'success' | 'warning' | 'danger' | 'muted'; children: React.ReactNode }) {
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 10 }}>
      <IconBadge name={icon} tone={tone} size={16} />
      <Text style={{ fontFamily: fonts.bold, color: colors.text, fontSize: 15, flex: 1 }}>{children}</Text>
    </View>
  );
}

function LinkRow({ icon, label, onPress }: { icon: keyof typeof Ionicons.glyphMap; label: string; onPress: () => void }) {
  return (
    <Pressable
      onPress={onPress}
      style={({ pressed }) => [
        {
          flexDirection: 'row',
          alignItems: 'center',
          gap: 12,
          paddingVertical: 14,
          paddingHorizontal: 4,
          borderBottomWidth: 1,
          borderBottomColor: colors.border,
        },
        pressed && { opacity: 0.6 },
      ]}
    >
      <Ionicons name={icon} size={18} color={colors.textMuted} />
      <Text style={{ flex: 1, color: colors.text, fontFamily: fonts.medium, fontSize: 14.5 }}>{label}</Text>
      <Ionicons name="chevron-forward" size={16} color={colors.textFaint} />
    </Pressable>
  );
}

const IDENTITY_STATUS_LABELS: Record<string, string> = {
  PENDING: 'Đang chờ admin duyệt',
  APPROVED: 'Đã xác minh',
  REJECTED: 'Bị từ chối',
};

const VIP_PLANS: { plan: VipPlan; label: string; priceCoins: number; description: string }[] = [
  { plan: 'MONTHLY', label: '1 tháng', priceCoins: 300, description: '300 xu mỗi tháng' },
  { plan: 'YEARLY', label: '1 năm', priceCoins: 2_500, description: '2.500 xu mỗi năm — tiết kiệm hơn mua theo tháng' },
  { plan: 'LIFETIME', label: 'Vĩnh viễn', priceCoins: 8_000, description: '8.000 xu, thanh toán một lần, sử dụng vĩnh viễn' },
];

const PRIVACY_POLICY_URL = 'https://loot.com.vn/privacy.html';
const TERMS_URL = 'https://loot.com.vn/terms.html';
const GUIDE_URL = 'https://loot.com.vn/huong-dan.html';
const ABOUT_URL = 'https://loot.com.vn/gioi-thieu.html';
const TASK_TYPES_URL = 'https://loot.com.vn/nhiem-vu.html';
const WEB_APP_URL = 'https://loot.com.vn/app';

// VIP is a purely in-app digital perk paid for with coins that were topped up
// via bank transfer (outside Apple/Google's payment system) — on a native
// build that risks an App Store review rejection (Guideline 3.1.1: digital
// content/features must use in-app purchase, unless it's payment for a real-
// world good/service like posting a task, which VIP isn't). So the purchase
// flow only runs on the web build; native app users are pointed to the site.
const CAN_BUY_VIP_IN_APP = Platform.OS === 'web';

const ROLE_LABELS: Record<string, string> = {
  REVIEWER: 'Người nhận việc',
  SHOP_OWNER: 'Người đăng việc',
  ADMIN: 'Quản trị viên',
};

const POSTER_KIND_LABELS: Record<string, string> = {
  INDIVIDUAL: 'Cá nhân',
  BUSINESS: 'Doanh nghiệp',
};

export default function ProfileScreen() {
  const { user, token, activeMode, setActiveMode, addRole, logout, refreshUser } = useAuth();
  const { mode, setMode } = useThemeMode();
  const router = useRouter();
  const isAdmin = user?.role === 'ADMIN';
  const roles = user?.roles ?? (user ? [user.role] : []);
  const hasReviewerRole = roles.includes('REVIEWER');
  const hasOwnerRole = roles.includes('SHOP_OWNER');
  const isReviewer = hasReviewerRole;

  const [referralCopied, setReferralCopied] = useState(false);
  const [identityIdNumber, setIdentityIdNumber] = useState('');
  const [payoutBankName, setPayoutBankName] = useState(user?.payoutBankName ?? '');
  const [payoutAccountNumber, setPayoutAccountNumber] = useState(user?.payoutAccountNumber ?? '');
  const [payoutAccountHolderName, setPayoutAccountHolderName] = useState(user?.payoutAccountHolderName ?? '');
  const [payoutSaving, setPayoutSaving] = useState(false);
  const [payoutError, setPayoutError] = useState<string | null>(null);
  const [payoutMessage, setPayoutMessage] = useState<string | null>(null);
  const [dataConsent, setDataConsent] = useState(false);
  const [vipBuying, setVipBuying] = useState<VipPlan | null>(null);
  const [vipError, setVipError] = useState<string | null>(null);
  const [pendingVipPlan, setPendingVipPlan] = useState<VipPlan | null>(null);
  const [addingRole, setAddingRole] = useState(false);
  const [addRoleError, setAddRoleError] = useState<string | null>(null);
  const [showIdentityCamera, setShowIdentityCamera] = useState(false);
  const [identityUploading, setIdentityUploading] = useState(false);
  const [identityError, setIdentityError] = useState<string | null>(null);
  const [identityPhotoToken, setIdentityPhotoToken] = useState<string | null>(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarError, setAvatarError] = useState<string | null>(null);

  useEffect(() => {
    if (!token || !user?.identityPhotoUrl) {
      setIdentityPhotoToken(null);
      return;
    }
    let cancelled = false;
    getIdentityPhotoToken(token)
      .then((r) => {
        if (!cancelled) setIdentityPhotoToken(r.token);
      })
      .catch(() => {
        if (!cancelled) setIdentityPhotoToken(null);
      });
    return () => {
      cancelled = true;
    };
  }, [token, user?.identityPhotoUrl]);
  const [phoneInput, setPhoneInput] = useState(user?.phone ?? '');
  const [otpCode, setOtpCode] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [phoneBusy, setPhoneBusy] = useState(false);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [phoneMessage, setPhoneMessage] = useState<string | null>(null);
  const [emailOtpCode, setEmailOtpCode] = useState('');
  const [emailOtpSent, setEmailOtpSent] = useState(false);
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailMessage, setEmailMessage] = useState<string | null>(null);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const onAddRole = async (role: 'REVIEWER' | 'SHOP_OWNER') => {
    setAddRoleError(null);
    setAddingRole(true);
    try {
      await addRole(role);
    } catch (e) {
      setAddRoleError(e instanceof ApiError ? e.message : 'Thêm vai trò thất bại');
    } finally {
      setAddingRole(false);
    }
  };

  const onIdentityPhotoCaptured = async (uri: string) => {
    setShowIdentityCamera(false);
    if (!token) return;
    setIdentityError(null);
    setIdentityUploading(true);
    try {
      await uploadIdentityPhoto(token, uri);
      await refreshUser();
    } catch (e) {
      setIdentityError(e instanceof ApiError ? e.message : 'Tải ảnh thất bại');
    } finally {
      setIdentityUploading(false);
    }
  };

  const onPickAvatar = async () => {
    if (!token) return;
    setAvatarError(null);
    const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (status !== 'granted') {
      setAvatarError('Chưa được cấp quyền thư viện ảnh — vào Cài đặt để cấp quyền rồi thử lại.');
      return;
    }
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      allowsEditing: true,
      aspect: [1, 1],
      quality: 0.7,
    });
    if (result.canceled || !result.assets[0]) return;
    setAvatarUploading(true);
    try {
      await uploadAvatar(token, result.assets[0].uri);
      await refreshUser();
    } catch (e) {
      setAvatarError(e instanceof ApiError ? e.message : 'Tải ảnh đại diện thất bại');
    } finally {
      setAvatarUploading(false);
    }
  };

  const onRequestOtp = async () => {
    if (!token || !phoneInput.trim()) return;
    setPhoneError(null);
    setPhoneMessage(null);
    setPhoneBusy(true);
    try {
      await requestPhoneOtp(token, phoneInput.trim());
      setOtpSent(true);
      setPhoneMessage('Đã gửi mã OTP, kiểm tra tin nhắn điện thoại');
    } catch (e) {
      setPhoneError(e instanceof ApiError ? e.message : 'Gửi mã OTP thất bại');
    } finally {
      setPhoneBusy(false);
    }
  };

  const onVerifyOtp = async () => {
    if (!token || otpCode.trim().length !== 6) return;
    setPhoneError(null);
    setPhoneBusy(true);
    try {
      await verifyPhoneOtp(token, otpCode.trim());
      await refreshUser();
      setOtpSent(false);
      setOtpCode('');
      setPhoneMessage('Xác minh số điện thoại thành công');
    } catch (e) {
      setPhoneError(e instanceof ApiError ? e.message : 'Mã OTP không đúng');
    } finally {
      setPhoneBusy(false);
    }
  };

  const onRequestEmailOtp = async () => {
    if (!token) return;
    setEmailError(null);
    setEmailMessage(null);
    setEmailBusy(true);
    try {
      await requestEmailOtp(token);
      setEmailOtpSent(true);
      setEmailMessage('Đã gửi mã OTP, kiểm tra hộp thư email');
    } catch (e) {
      setEmailError(e instanceof ApiError ? e.message : 'Gửi mã OTP thất bại');
    } finally {
      setEmailBusy(false);
    }
  };

  const onVerifyEmailOtp = async () => {
    if (!token || emailOtpCode.trim().length !== 6) return;
    setEmailError(null);
    setEmailBusy(true);
    try {
      await verifyEmailOtp(token, emailOtpCode.trim());
      await refreshUser();
      setEmailOtpSent(false);
      setEmailOtpCode('');
      setEmailMessage('Xác minh email thành công');
    } catch (e) {
      setEmailError(e instanceof ApiError ? e.message : 'Mã OTP không đúng');
    } finally {
      setEmailBusy(false);
    }
  };

  const savePayoutInfo = async () => {
    if (!token) return;
    setPayoutError(null);
    setPayoutMessage(null);
    // Mirrors UpdatePayoutInfoDto — all fields are optional, but if filled
    // in must match the backend's format, so catch it here instead of
    // sending straight through and waiting on a round trip.
    if (identityIdNumber && !/^\d{9}(\d{3})?$/.test(identityIdNumber)) {
      setPayoutError('Số CCCD/CMND phải có 9 hoặc 12 chữ số');
      return;
    }
    if (payoutBankName && payoutBankName.trim().length < 2) {
      setPayoutError('Tên ngân hàng phải có ít nhất 2 ký tự');
      return;
    }
    if (payoutAccountNumber && (payoutAccountNumber.length < 6 || payoutAccountNumber.length > 30)) {
      setPayoutError('Số tài khoản phải có từ 6 đến 30 ký tự');
      return;
    }
    if (payoutAccountHolderName && payoutAccountHolderName.trim().length < 2) {
      setPayoutError('Tên chủ tài khoản phải có ít nhất 2 ký tự');
      return;
    }
    setPayoutSaving(true);
    try {
      await updatePayoutInfo(token, {
        dataConsent,
        identityIdNumber: identityIdNumber || undefined,
        payoutBankName: payoutBankName || undefined,
        payoutAccountNumber: payoutAccountNumber || undefined,
        payoutAccountHolderName: payoutAccountHolderName || undefined,
      });
      await refreshUser();
      setPayoutMessage('Đã lưu thông tin nhận tiền');
      setIdentityIdNumber('');
    } catch (e) {
      setPayoutError(e instanceof ApiError ? e.message : 'Lưu thất bại');
    } finally {
      setPayoutSaving(false);
    }
  };

  const onBuyVip = (plan: VipPlan) => {
    setPendingVipPlan(plan);
  };

  const confirmBuyVip = async () => {
    if (!token || !pendingVipPlan) return;
    const plan = pendingVipPlan;
    setVipError(null);
    setVipBuying(plan);
    try {
      await purchaseVip(token, plan);
      await refreshUser();
    } catch (e) {
      setVipError(e instanceof ApiError ? e.message : 'Mua VIP thất bại');
    } finally {
      setVipBuying(null);
      setPendingVipPlan(null);
    }
  };

  const confirmDeleteAccount = async () => {
    if (!token || !deletePassword) return;
    setDeleteError(null);
    setDeleteBusy(true);
    try {
      await deleteOwnAccount(token, deletePassword);
      setDeleteConfirmVisible(false);
      await logout();
    } catch (e) {
      setDeleteError(e instanceof ApiError ? e.message : 'Xoá tài khoản thất bại');
    } finally {
      setDeleteBusy(false);
    }
  };

  const copyReferralCode = async () => {
    if (!user?.referralCode) return;
    await Clipboard.setStringAsync(user.referralCode);
    setReferralCopied(true);
    setTimeout(() => setReferralCopied(false), 1500);
  };

  if (showIdentityCamera) {
    return <CameraCapture onCapture={onIdentityPhotoCaptured} onCancel={() => setShowIdentityCamera(false)} />;
  }

  return (
    <ScreenContainer safeTop safeBottom>
      <ScrollView>
        <GradientCard colors={gradients.hero} style={{ flexDirection: 'row', alignItems: 'center', gap: 14 }}>
          <Pressable onPress={onPickAvatar} disabled={avatarUploading}>
            <View
              style={{
                width: 56,
                height: 56,
                borderRadius: 28,
                backgroundColor: 'rgba(255,255,255,0.22)',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
              }}
            >
              {avatarUploading ? (
                <ActivityIndicator color="#fff" />
              ) : user?.avatarUrl ? (
                <Image source={{ uri: resolvePhotoUrl(user.avatarUrl) }} style={{ width: 56, height: 56 }} />
              ) : (
                <Text style={{ color: '#fff', fontFamily: fonts.extraBold, fontSize: 22 }}>
                  {(user?.name ?? '?').trim().charAt(0).toUpperCase()}
                </Text>
              )}
            </View>
            <View
              style={{
                position: 'absolute',
                bottom: -2,
                right: -2,
                width: 20,
                height: 20,
                borderRadius: 10,
                backgroundColor: colors.card,
                alignItems: 'center',
                justifyContent: 'center',
                borderWidth: 1.5,
                borderColor: colors.primary,
              }}
            >
              <Ionicons name="camera" size={11} color={colors.primary} />
            </View>
          </Pressable>
          <View style={{ flex: 1 }}>
            <Text style={{ fontSize: 18, fontFamily: fonts.bold, color: '#fff' }}>{user?.name}</Text>
            <Text style={{ color: 'rgba(255,255,255,0.8)', marginTop: 2, fontSize: 13 }}>{user?.email}</Text>
            <View
              style={{
                alignSelf: 'flex-start',
                backgroundColor: 'rgba(255,255,255,0.18)',
                borderRadius: 999,
                paddingHorizontal: 10,
                paddingVertical: 3,
                marginTop: 8,
              }}
            >
              <Text style={{ color: '#fff', fontFamily: fonts.semiBold, fontSize: 11.5 }}>
                {roles.map((r) => ROLE_LABELS[r]).join(' · ')}
                {hasOwnerRole && user?.posterKind ? ` · ${POSTER_KIND_LABELS[user.posterKind]}` : ''}
              </Text>
            </View>
          </View>
        </GradientCard>
        <ErrorText>{avatarError}</ErrorText>

        {!isAdmin && hasReviewerRole && hasOwnerRole ? (
          <Card>
            <SectionTitle icon="swap-horizontal-outline">Đang dùng với vai trò</SectionTitle>
            <View style={{ flexDirection: 'row', gap: 8 }}>
              <Pressable
                onPress={() => setActiveMode('REVIEWER')}
                style={{
                  flex: 1,
                  paddingVertical: 10,
                  borderRadius: 10,
                  borderWidth: 1.5,
                  alignItems: 'center',
                  borderColor: activeMode === 'REVIEWER' ? colors.primary : colors.border,
                  backgroundColor: activeMode === 'REVIEWER' ? colors.primarySoft : colors.card,
                }}
              >
                <Text style={{ fontFamily: fonts.semiBold, color: activeMode === 'REVIEWER' ? colors.primaryDark : colors.text }}>
                  Người nhận việc
                </Text>
              </Pressable>
              <Pressable
                onPress={() => setActiveMode('SHOP_OWNER')}
                style={{
                  flex: 1,
                  paddingVertical: 10,
                  borderRadius: 10,
                  borderWidth: 1.5,
                  alignItems: 'center',
                  borderColor: activeMode === 'SHOP_OWNER' ? colors.primary : colors.border,
                  backgroundColor: activeMode === 'SHOP_OWNER' ? colors.primarySoft : colors.card,
                }}
              >
                <Text style={{ fontFamily: fonts.semiBold, color: activeMode === 'SHOP_OWNER' ? colors.primaryDark : colors.text }}>
                  Người đăng việc
                </Text>
              </Pressable>
            </View>
          </Card>
        ) : null}

        {!isAdmin && (!hasReviewerRole || !hasOwnerRole) ? (
          <Card>
            <SectionTitle icon="add-circle-outline">
              {hasReviewerRole ? 'Muốn đăng việc?' : 'Muốn nhận việc kiếm thêm?'}
            </SectionTitle>
            <Text style={{ color: colors.textMuted, marginBottom: 10 }}>
              Bạn có thể dùng cùng một tài khoản này để{' '}
              {hasReviewerRole ? 'đăng nhiệm vụ thuê người làm' : 'nhận và làm nhiệm vụ kiếm xu'}, không
              cần đăng ký tài khoản mới.
            </Text>
            <ErrorText>{addRoleError}</ErrorText>
            <Button
              title={hasReviewerRole ? 'Trở thành người đăng việc' : 'Trở thành người nhận việc'}
              variant="outline"
              onPress={() => onAddRole(hasReviewerRole ? 'SHOP_OWNER' : 'REVIEWER')}
              loading={addingRole}
            />
          </Card>
        ) : null}

        {!isAdmin ? (
          <Card>
            <SectionTitle
              icon="shield-checkmark-outline"
              tone={user?.identityVerificationStatus === 'APPROVED' ? 'success' : user?.identityVerificationStatus === 'REJECTED' ? 'danger' : 'primary'}
            >
              Xác minh danh tính (ảnh CCCD/CMND)
            </SectionTitle>
            <Text style={{ color: colors.textMuted, marginBottom: 10 }}>
              {user?.identityVerificationStatus
                ? `Trạng thái: ${IDENTITY_STATUS_LABELS[user.identityVerificationStatus] ?? user.identityVerificationStatus}`
                : 'Chưa gửi ảnh xác minh. Ảnh này giúp Loot xác nhận đúng chủ tài khoản khi có tranh chấp (ví dụ hai người cùng nhận là chủ một shop).'}
            </Text>
            {user?.identityVerificationStatus === 'REJECTED' && user.identityVerificationNote ? (
              <Text style={{ color: colors.danger, fontSize: 13, marginBottom: 10 }}>
                Lý do từ chối: {user.identityVerificationNote}
              </Text>
            ) : null}
            {user?.identityPhotoUrl && identityPhotoToken ? (
              <Image
                source={{ uri: myIdentityPhotoUrl(identityPhotoToken) }}
                style={{ width: 120, height: 80, borderRadius: 8, marginBottom: 10 }}
              />
            ) : null}
            <ErrorText>{identityError}</ErrorText>
            {!user?.hasIdentityOnFile ? (
              <Text style={{ color: colors.textMuted, fontSize: 12, marginBottom: 10 }}>
                Cần khai số CCCD/CMND và đồng ý Chính sách quyền riêng tư ở mục bên dưới trước khi gửi ảnh.
              </Text>
            ) : null}
            <Button
              title={user?.identityPhotoUrl ? 'Chụp lại ảnh CCCD/CMND' : 'Chụp ảnh CCCD/CMND'}
              variant="outline"
              onPress={() => {
                // Previously a silently `disabled` button when hasIdentityOnFile
                // was false — looked like a dead/broken button with zero
                // feedback about why. Always tappable now; explain the missing
                // prerequisite instead of just refusing the tap.
                if (!user?.hasIdentityOnFile) {
                  Alert.alert(
                    'Cần khai số CCCD/CMND trước',
                    'Nhập số CCCD/CMND và đồng ý Chính sách quyền riêng tư ở mục "Thông tin nhận tiền" bên dưới, sau đó quay lại đây để chụp ảnh.',
                  );
                  return;
                }
                setShowIdentityCamera(true);
              }}
              loading={identityUploading}
              disabled={user?.identityVerificationStatus === 'APPROVED'}
            />
          </Card>
        ) : null}

        {!isAdmin ? (
          <Card>
            <SectionTitle icon="call-outline" tone={user?.phoneVerified ? 'success' : 'primary'}>
              Xác minh số điện thoại{user?.phoneVerified ? ' — Đã xác minh' : ''}
            </SectionTitle>
            {!user?.phoneVerified ? (
              <>
                <Label>Số điện thoại</Label>
                <FormInput
                  value={phoneInput}
                  onChangeText={setPhoneInput}
                  keyboardType="phone-pad"
                  placeholder="09xxxxxxxx"
                  editable={!otpSent}
                />
                <ErrorText>{phoneError}</ErrorText>
                {phoneMessage ? (
                  <Text style={{ color: colors.success, marginBottom: 10 }}>{phoneMessage}</Text>
                ) : null}
                {!otpSent ? (
                  <Button
                    title="Gửi mã OTP"
                    variant="outline"
                    onPress={onRequestOtp}
                    loading={phoneBusy}
                    disabled={!phoneInput.trim()}
                  />
                ) : (
                  <>
                    <Label>Mã OTP (6 số)</Label>
                    <FormInput
                      value={otpCode}
                      onChangeText={setOtpCode}
                      keyboardType="number-pad"
                      placeholder="123456"
                    />
                    <Button title="Xác nhận mã OTP" onPress={onVerifyOtp} loading={phoneBusy} disabled={otpCode.trim().length !== 6} />
                    <Button title="Gửi lại mã" variant="outline" onPress={onRequestOtp} loading={phoneBusy} />
                  </>
                )}
              </>
            ) : (
              <Text style={{ color: colors.textMuted }}>Số điện thoại: {user.phone}</Text>
            )}
          </Card>
        ) : null}

        {!isAdmin ? (
          <Card>
            <SectionTitle icon="mail-outline" tone={user?.emailVerified ? 'success' : 'primary'}>
              Xác minh email{user?.emailVerified ? ' — Đã xác minh' : ''}
            </SectionTitle>
            {!user?.emailVerified ? (
              <>
                <Text style={{ color: colors.textMuted, fontSize: 12, marginBottom: 10 }}>
                  Gửi mã OTP đến email đăng ký: {user?.email}
                </Text>
                <ErrorText>{emailError}</ErrorText>
                {emailMessage ? (
                  <Text style={{ color: colors.success, marginBottom: 10 }}>{emailMessage}</Text>
                ) : null}
                {!emailOtpSent ? (
                  <Button title="Gửi mã OTP" variant="outline" onPress={onRequestEmailOtp} loading={emailBusy} />
                ) : (
                  <>
                    <Label>Mã OTP (6 số)</Label>
                    <FormInput
                      value={emailOtpCode}
                      onChangeText={setEmailOtpCode}
                      keyboardType="number-pad"
                      placeholder="123456"
                    />
                    <Button
                      title="Xác nhận mã OTP"
                      onPress={onVerifyEmailOtp}
                      loading={emailBusy}
                      disabled={emailOtpCode.trim().length !== 6}
                    />
                    <Button title="Gửi lại mã" variant="outline" onPress={onRequestEmailOtp} loading={emailBusy} />
                  </>
                )}
              </>
            ) : (
              <Text style={{ color: colors.textMuted }}>Email: {user.email}</Text>
            )}
          </Card>
        ) : null}

        {user?.referralCode ? (
          <Card>
            <SectionTitle icon="gift-outline">Mã giới thiệu của bạn</SectionTitle>
            <Text
              style={{
                fontFamily: fonts.bold,
                color: colors.primary,
                fontSize: 22,
                letterSpacing: 1,
                marginBottom: 8,
              }}
              selectable
            >
              {user.referralCode}
            </Text>
            <Text style={{ color: colors.textMuted, marginBottom: 10 }}>
              Sao chép và gửi cho bạn bè — họ nhập mã này khi đăng ký để bạn nhận thưởng khi đủ điều kiện.
            </Text>
            <Button
              title={referralCopied ? 'Đã sao chép' : 'Sao chép mã'}
              onPress={copyReferralCode}
            />
          </Card>
        ) : null}

        {hasOwnerRole && user?.ownerTier ? (
          <Card>
            <SectionTitle icon="ribbon-outline" tone="warning">
              Hạng thành viên: {OWNER_TIER_LABELS[user.ownerTier]}
            </SectionTitle>
            <Text style={{ color: colors.textMuted }}>
              Phí dịch vụ khi tạo nhiệm vụ: {user.ownerEscrowFeePercent}% — nạp càng nhiều, phí càng thấp.
            </Text>
          </Card>
        ) : null}

        {hasOwnerRole && user ? (
          <Card>
            <SectionTitle icon="star" tone={user.vipActive ? 'warning' : 'muted'}>
              VIP{user.vipActive ? ' — Đang hoạt động' : ''}
            </SectionTitle>
            <Text style={{ color: colors.textMuted, marginBottom: 10 }}>
              {user.vipIsLifetime
                ? 'Bạn đang sở hữu VIP vĩnh viễn.'
                : user.vipActive && user.vipExpiresAt
                  ? `Gói VIP của bạn hết hạn vào ngày ${new Date(user.vipExpiresAt).toLocaleDateString('vi-VN')}.`
                  : 'Mua VIP để mở các tính năng yêu cầu độ uy tín cao (như thưởng theo % đơn hàng) mà không cần chờ lên hạng bằng cách nạp tiền.'}
            </Text>
            <ErrorText>{vipError}</ErrorText>
            {CAN_BUY_VIP_IN_APP ? (
              VIP_PLANS.map((p) => {
                const isDisabled = vipBuying !== null || (user.vipIsLifetime ?? false);
                return (
                  <Pressable
                    key={p.plan}
                    onPress={() => onBuyVip(p.plan)}
                    disabled={isDisabled}
                    style={({ pressed }) => ({
                      borderWidth: 1.5,
                      borderColor: colors.primary,
                      borderRadius: 12,
                      paddingVertical: 12,
                      paddingHorizontal: 14,
                      marginBottom: 10,
                      opacity: isDisabled ? 0.5 : pressed ? 0.85 : 1,
                    })}
                  >
                    {vipBuying === p.plan ? (
                      <ActivityIndicator color={colors.primary} />
                    ) : (
                      <>
                        <Text style={{ fontFamily: fonts.semiBold, fontSize: 15.5, color: colors.primary }}>
                          {p.label}
                        </Text>
                        <Text style={{ fontSize: 13, color: colors.textMuted, marginTop: 2 }}>
                          {p.description}
                        </Text>
                      </>
                    )}
                  </Pressable>
                );
              })
            ) : !user.vipIsLifetime ? (
              <Button
                title="Mua VIP trên trình duyệt"
                variant="outline"
                onPress={() => openExternalUrl(WEB_APP_URL)}
              />
            ) : null}
          </Card>
        ) : null}

        {isReviewer && user?.reviewerTier ? (
          <Card>
            <SectionTitle icon="trophy-outline" tone="warning">
              Hạng uy tín: {REVIEWER_TIER_LABELS[user.reviewerTier]}
            </SectionTitle>
            <Text style={{ color: colors.textMuted }}>
              Đã hoàn thành {user.completedTaskCount} việc · Phí rút tiền {user.withdrawalFeePercent}%
            </Text>
          </Card>
        ) : null}

        {isReviewer ? (
          <Card>
            <SectionTitle icon="card-outline">Thông tin nhận tiền (bắt buộc để rút xu)</SectionTitle>
            <Label>Số CCCD</Label>
            <FormInput
              value={identityIdNumber}
              onChangeText={setIdentityIdNumber}
              keyboardType="numeric"
              placeholder={user?.hasIdentityOnFile ? 'Đã có trên hồ sơ — nhập để cập nhật' : '9 hoặc 12 số'}
            />
            <Label>Ngân hàng</Label>
            <FormInput value={payoutBankName} onChangeText={setPayoutBankName} placeholder="Vietcombank" />
            <Label>Số tài khoản</Label>
            <FormInput
              value={payoutAccountNumber}
              onChangeText={setPayoutAccountNumber}
              placeholder="0071000012345"
            />
            <Label>Tên chủ tài khoản</Label>
            <FormInput
              value={payoutAccountHolderName}
              onChangeText={setPayoutAccountHolderName}
              placeholder="NGUYEN VAN A"
              autoCapitalize="characters"
            />
            <Checkbox
              checked={dataConsent}
              onToggle={() => setDataConsent((v) => !v)}
              label={
                <>
                  Tôi đồng ý cho Loot thu thập số CCCD và thông tin tài khoản ngân hàng để xác minh danh
                  tính và chi trả xu, theo{' '}
                  <Text
                    style={{ color: colors.primary, textDecorationLine: 'underline' }}
                    onPress={() => openExternalUrl(PRIVACY_POLICY_URL)}
                  >
                    Chính sách quyền riêng tư
                  </Text>{' '}
                  và{' '}
                  <Text
                    style={{ color: colors.primary, textDecorationLine: 'underline' }}
                    onPress={() => openExternalUrl(TERMS_URL)}
                  >
                    Điều khoản sử dụng
                  </Text>
                  .
                </>
              }
            />
            <ErrorText>{payoutError}</ErrorText>
            {payoutMessage ? (
              <Text style={{ color: colors.success, marginBottom: 10 }}>{payoutMessage}</Text>
            ) : null}
            <Button
              title="Lưu thông tin nhận tiền"
              onPress={savePayoutInfo}
              loading={payoutSaving}
              disabled={!dataConsent}
            />
          </Card>
        ) : null}

        {isAdmin ? (
          <Card>
            <SectionTitle icon="settings-outline">Công cụ quản trị</SectionTitle>
            <LinkRow icon="people-outline" label="Quản lý người dùng" onPress={() => router.push('/admin-users')} />
            <LinkRow icon="cash-outline" label="Duyệt yêu cầu nạp tiền" onPress={() => router.push('/admin-topups')} />
            <LinkRow icon="arrow-down-circle-outline" label="Duyệt yêu cầu rút tiền" onPress={() => router.push('/admin-withdrawals')} />
            <LinkRow icon="phone-portrait-outline" label="Tài khoản trùng thiết bị" onPress={() => router.push('/admin-duplicate-devices')} />
            <LinkRow icon="images-outline" label="Ảnh trùng lặp" onPress={() => router.push('/admin-duplicate-photos')} />
            <LinkRow icon="shield-checkmark-outline" label="Duyệt xác minh danh tính" onPress={() => router.push('/admin-identity-verifications')} />
          </Card>
        ) : null}

        <Card>
          <SectionTitle icon="contrast-outline">Giao diện</SectionTitle>
          <View style={{ flexDirection: 'row', gap: 6 }}>
            <Pressable
              onPress={() => setMode('light')}
              style={{
                flex: 1,
                flexDirection: 'row',
                paddingVertical: 10,
                borderRadius: 10,
                borderWidth: 1.5,
                alignItems: 'center',
                justifyContent: 'center',
                gap: 5,
                borderColor: mode === 'light' ? colors.primary : colors.border,
                backgroundColor: mode === 'light' ? colors.primarySoft : colors.card,
              }}
            >
              <Ionicons name="sunny-outline" size={16} color={mode === 'light' ? colors.primaryDark : colors.textMuted} />
              <Text style={{ fontSize: 13, fontFamily: fonts.semiBold, color: mode === 'light' ? colors.primaryDark : colors.text }}>
                Sáng
              </Text>
            </Pressable>
            <Pressable
              onPress={() => setMode('dark')}
              style={{
                flex: 1,
                flexDirection: 'row',
                paddingVertical: 10,
                borderRadius: 10,
                borderWidth: 1.5,
                alignItems: 'center',
                justifyContent: 'center',
                gap: 5,
                borderColor: mode === 'dark' ? colors.primary : colors.border,
                backgroundColor: mode === 'dark' ? colors.primarySoft : colors.card,
              }}
            >
              <Ionicons name="moon-outline" size={16} color={mode === 'dark' ? colors.primaryDark : colors.textMuted} />
              <Text style={{ fontSize: 13, fontFamily: fonts.semiBold, color: mode === 'dark' ? colors.primaryDark : colors.text }}>
                Tối
              </Text>
            </Pressable>
            <Pressable
              onPress={() => setMode('mocha')}
              style={{
                flex: 1,
                flexDirection: 'row',
                paddingVertical: 10,
                borderRadius: 10,
                borderWidth: 1.5,
                alignItems: 'center',
                justifyContent: 'center',
                gap: 5,
                borderColor: mode === 'mocha' ? colors.primary : colors.border,
                backgroundColor: mode === 'mocha' ? colors.primarySoft : colors.card,
              }}
            >
              <Ionicons name="cafe-outline" size={16} color={mode === 'mocha' ? colors.primaryDark : colors.textMuted} />
              <Text style={{ fontSize: 13, fontFamily: fonts.semiBold, color: mode === 'mocha' ? colors.primaryDark : colors.text }}>
                Mocha
              </Text>
            </Pressable>
          </View>
        </Card>

        <Card>
          <LinkRow icon="information-circle-outline" label="Giới thiệu về Loot" onPress={() => openExternalUrl(ABOUT_URL)} />
          <LinkRow icon="list-outline" label="Các loại nhiệm vụ" onPress={() => openExternalUrl(TASK_TYPES_URL)} />
          <LinkRow icon="book-outline" label="Hướng dẫn sử dụng và biểu phí" onPress={() => openExternalUrl(GUIDE_URL)} />
        </Card>

        <Button title="Đăng xuất" variant="outline" onPress={logout} />
        {!isAdmin ? (
          <Pressable onPress={() => setDeleteConfirmVisible(true)} style={{ alignItems: 'center', paddingVertical: 10 }}>
            <Text style={{ color: colors.danger, fontFamily: fonts.medium, fontSize: 13 }}>Xoá tài khoản</Text>
          </Pressable>
        ) : null}
      </ScrollView>
      <ConfirmDialog
        visible={pendingVipPlan !== null}
        title="Xác nhận mua VIP"
        message={
          pendingVipPlan
            ? `Bạn có muốn mua gói VIP "${VIP_PLANS.find((p) => p.plan === pendingVipPlan)?.label}" với giá ${VIP_PLANS.find((p) => p.plan === pendingVipPlan)?.priceCoins} xu không?`
            : ''
        }
        confirmLabel="Mua"
        loading={vipBuying !== null}
        onConfirm={confirmBuyVip}
        onCancel={() => setPendingVipPlan(null)}
      />
      <ConfirmDialog
        visible={deleteConfirmVisible}
        title="Xoá tài khoản"
        message="Toàn bộ thông tin cá nhân (tên, email, SĐT, CCCD, thông tin nhận tiền) sẽ bị xoá vĩnh viễn và bạn không thể đăng nhập lại. Nếu ví còn xu, hãy dùng hết hoặc rút hết trước. Hành động này không thể hoàn tác."
        confirmLabel="Xoá vĩnh viễn"
        danger
        loading={deleteBusy}
        onConfirm={confirmDeleteAccount}
        onCancel={() => {
          setDeleteConfirmVisible(false);
          setDeletePassword('');
          setDeleteError(null);
        }}
      >
        <View style={{ marginTop: 10 }}>
          <FormInput
            value={deletePassword}
            onChangeText={setDeletePassword}
            placeholder="Nhập mật khẩu để xác nhận"
            secureTextEntry
            autoFocus
          />
          <ErrorText>{deleteError}</ErrorText>
        </View>
      </ConfirmDialog>
    </ScreenContainer>
  );
}
